// Publisher socials: top cards (per platform) + table of websites and their social accounts.

import React, { useEffect, useState } from "react"
import {
  Card,
  CardBody,
  CardTitle,
  Col,
  Row,
  Spinner,
} from "reactstrap"
import Breadcrumb from "../../components/Common/Breadcrumb"
import { useLocation, useNavigate } from "react-router-dom"
import { CTooltip } from "@coreui/react"
import SEO from "../../components/Common/SEO"
import { MDBDataTable } from "mdbreact"
import "../../assets/scss/datatables.scss"
import "../../components/PublisherAnalysis/style/publisherHeader.scss"
import axios from "axios"
import { api } from "../../utils/variables"

export default function PublisherSocials() {
  // Routing / query params
  const navigate = useNavigate()
  const location = useLocation()
  const queryParams = new URLSearchParams(location.search)
  const publisherNameFromUrl = queryParams.get("publisher_name")
  const gameID = queryParams.get("item_id")

  // Local state
  const [loader, setLoader] = useState(true)
  const [publisherName, setPublisherName] = useState("")
  // Publisher-level socials by normalized platform key (e.g. "x", "youtube")
  const [publisherSocials, setPublisherSocials] = useState({}) // Dynamic: any platform
  // Per-website (studio/app) rows for the table
  const [appsSocialData, setAppsSocialData] = useState([])
  // All normalized platform keys used for cards + table columns
  const [availablePlatforms, setAvailablePlatforms] = useState([]) // Track all platforms found in data
  const [error, setError] = useState(null)

  // Map a platform name to icon, color, label and normalized key (e.g. "twitter" -> "x")
  const getPlatformMetadata = (platformName) => {
    const platform = platformName.toLowerCase()
    const metadata = {
      youtube: {
        icon: "fab fa-youtube",
        color: "#FF0000",
        displayName: "YouTube",
        key: "youtube"
      },
      x: {
        icon: "fab fa-twitter",
        color: "#1DA1F2",
        displayName: "X (Twitter)",
        key: "x"
      },
      twitter: {
        icon: "fab fa-twitter",
        color: "#1DA1F2",
        displayName: "X (Twitter)",
        key: "x"
      },
      instagram: {
        icon: "fab fa-instagram",
        color: "#E4405F",
        displayName: "Instagram",
        key: "instagram"
      },
      facebook: {
        icon: "fab fa-facebook",
        color: "#1877F2",
        displayName: "Facebook",
        key: "facebook"
      },
      linkedin: {
        icon: "fab fa-linkedin",
        color: "#0077B5",
        displayName: "LinkedIn",
        key: "linkedin"
      },
      tiktok: {
        icon: "fab fa-tiktok",
        color: "#000000",
        displayName: "TikTok",
        key: "tiktok"
      },
      snapchat: {
        icon: "fab fa-snapchat",
        color: "#FFFC00",
        displayName: "Snapchat",
        key: "snapchat"
      },
      discord: {
        icon: "fab fa-discord",
        color: "#5865F2",
        displayName: "Discord",
        key: "discord"
      },
      reddit: {
        icon: "fab fa-reddit",
        color: "#FF4500",
        displayName: "Reddit",
        key: "reddit"
      },
      twitch: {
        icon: "fab fa-twitch",
        color: "#9146FF",
        displayName: "Twitch",
        key: "twitch"
      }
    }

    // Return metadata if found, otherwise create default
    if (metadata[platform]) {
      return metadata[platform]
    }

    // Default for unknown platforms
    return {
      icon: "fab fa-globe",
      color: "#6c757d",
      displayName: platformName.charAt(0).toUpperCase() + platformName.slice(1),
      key: platform
    }
  }

  // Initial load: prefer publisher_name from URL, else resolve via item_id
  useEffect(() => {
    if (publisherNameFromUrl) {
      // Use publisher_name directly if provided
      setPublisherName(publisherNameFromUrl)
      fetchSocialsData(publisherNameFromUrl)
    } else if (gameID) {
      // Fallback to item_id if publisher_name is not provided
      fetchPublisherNameAndSocials(gameID)
    } else {
      setLoader(false)
    }
  }, [publisherNameFromUrl, gameID])

  // Fetch socials when we already know the publisher name
  const fetchSocialsData = async (publisherName) => {
    try {
      setLoader(true)
      setError(null)

      // API call: socials by publisher name
      const socialsRes = await axios.get(
        `${api}/tracker/publisher/socials?publisher_name=${encodeURIComponent(publisherName)}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("authToken")}`,
          },
        }
      )

      if (socialsRes.data?.data) {
        transformSocialData(socialsRes.data.data, publisherName)
      }
    } catch (err) {
      console.error("Error fetching publisher social data:", err)
      setError("Failed to load social data. Please try again later.")
    } finally {
      setLoader(false)
    }
  }

  // When we only have item_id, resolve publisher name first, then fetch socials
  const fetchPublisherNameAndSocials = async (itemId) => {
    try {
      setLoader(true)
      setError(null)

      // 1) Get publisher data from tracking endpoint
      const publisherRes = await axios.get(
        `${api}/tracker/tracking/publisher?item_id=${itemId}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("authToken")}`,
          },
        }
      )

      // 2) Get publisher name from the first app's details
      const games = publisherRes.data?.data || []
      let name = "Unknown Publisher"

      if (games.length > 0) {
        try {
          const appDetailsRes = await axios.get(`${api}/tracker/app/${games[0].item_id}`)
          name = appDetailsRes.data?.details?.publisher || name
        } catch (err) {
          console.error("Error fetching app details:", err)
        }
      }

      setPublisherName(name)

      // 3) Fetch socials using the resolved publisher name
      await fetchSocialsData(name)
    } catch (err) {
      console.error("Error fetching publisher data:", err)
      setError("Failed to load publisher data. Please try again later.")
    } finally {
      setLoader(false)
    }
  }

  // Transform API payload into:
  // - `publisherSocials` (cards)
  // - `appsSocialData` (table rows grouped by website)
  const transformSocialData = (data, publisherNameForTable) => {
    // Prefer explicit publisher name, else state
    const actualPublisherName = publisherNameForTable || publisherName || "Unknown Publisher"

    // Publisher-level socials (card section)
    const socials = {}
    const platformsSet = new Set()

    // Map platforms from API response (one entry per platform)
    data.forEach((platformData) => {
      const platformName = platformData.platform.toLowerCase()
      const normalizedKey = getPlatformMetadata(platformName).key

      const platformInfo = {
        handle: platformData.handle,
        url: platformData.profile_url,
        followers: platformData.followers,
        subscribers: platformData.metric_label === "Subscribers" ? platformData.followers : null,
        followersDisplay: platformData.followers_display,
        platform: platformData.platform, // Keep original platform name
      }

      socials[normalizedKey] = platformInfo
      platformsSet.add(normalizedKey)
    })

    setPublisherSocials(socials)
    setAvailablePlatforms(Array.from(platformsSet))

    // Per-website socials (table section)
    // Normalize site so variants of same domain group together
    const normalizeWebsite = (website) => {
      if (!website) return "unknown"
      try {
        const url = new URL(website)
        return url.hostname.replace("www.", "").toLowerCase()
      } catch {
        return website.toLowerCase()
      }
    }

    // Build accounts map grouped by normalized website
    const accountsByWebsite = {}

    data.forEach((platformData) => {
      const platformName = platformData.platform.toLowerCase()

      if (platformData.accounts && Array.isArray(platformData.accounts)) {
        platformData.accounts.forEach((account) => {
          const website = account.website || "Unknown"
          // Group by normalized website
          const websiteKey = normalizeWebsite(website)

          if (!accountsByWebsite[websiteKey]) {
            accountsByWebsite[websiteKey] = {
              publisherName: actualPublisherName, // Use actual publisher name from page title
              website: website,
            }
          }

          // Ensure platform array exists for this website
          const normalizedKey = getPlatformMetadata(platformName).key
          if (!accountsByWebsite[websiteKey][normalizedKey]) {
            accountsByWebsite[websiteKey][normalizedKey] = []
          }

          const socialInfo = {
            handle: account.handle,
            url: account.link,
            followers: account.followers,
          }

          accountsByWebsite[websiteKey][normalizedKey].push(socialInfo)
        })
      }
    })

    // Collect all platform keys present in any website
    const accountPlatformsSet = new Set()
    Object.values(accountsByWebsite).forEach(publisher => {
      Object.keys(publisher).forEach(key => {
        if (key !== 'publisherName' && key !== 'website' && Array.isArray(publisher[key])) {
          accountPlatformsSet.add(key)
        }
      })
    })

    // Merge platform keys from cards + table rows
    setAvailablePlatforms(prev => {
      const combined = new Set([...prev, ...Array.from(accountPlatformsSet)])
      return Array.from(combined)
    })

    // Convert map -> array for table rows (keep platform arrays)
    const publishersArray = Object.values(accountsByWebsite).map((publisher, index) => {
      const result = {
        item_id: `publisher-${index}`,
        publisherName: publisher.publisherName,
        website: publisher.website,
      }

      // Add all platform arrays
      Object.keys(publisher).forEach(key => {
        if (key !== 'publisherName' && key !== 'website') {
          result[key] = Array.isArray(publisher[key]) && publisher[key].length > 0
            ? publisher[key]
            : null
        }
      })

      return result
    })

    setAppsSocialData(publishersArray)
  }

  // Extract a readable name from a website URL (best effort)
  const extractPublisherNameFromWebsite = (website) => {
    if (!website) return "Unknown Publisher"
    try {
      const url = new URL(website)
      const hostname = url.hostname.replace("www.", "")
      // Extract publisher name from domain (e.g., "ghostsoftabor.com" -> "Ghostsoftabor")
      // Use the main domain name (first part before first dot)
      const parts = hostname.split(".")
      if (parts.length > 0) {
        const name = parts[0]
        // Capitalize and format the name properly
        return name
          .split(/[-_]/)
          .map(word => {
            // Handle camelCase or mixed case
            if (word.match(/^[A-Z]/)) {
              return word
            }
            return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
          })
          .join(" ")
      }
      return hostname
    } catch {
      // If URL parsing fails, try to extract from string
      const match = website.match(/https?:\/\/(?:www\.)?([^\/]+)/)
      if (match && match[1]) {
        const name = match[1].split(".")[0]
        return name
          .split(/[-_]/)
          .map(word => {
            if (word.match(/^[A-Z]/)) {
              return word
            }
            return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
          })
          .join(" ")
      }
      return website
    }
  }

  // Go back: prefer `location.state.from`, else browser history, else dashboard
  const handleGoBack = (e) => {
    e.preventDefault()
    // Prefer explicit "from" in navigation state, otherwise history, then fallback to dashboard
    try {
      const from = location && location.state && location.state.from
      if (from) {
        navigate(from)
        return
      }
    } catch (err) {
      // ignore
    }

    if (window && window.history && window.history.length > 1) {
      navigate(-1)
    } else {
      navigate("/dashboard")
    }
  }

  // Format follower/subscriber counts (e.g. 1540 -> "1.5K")
  const formatNumber = (num) => {
    if (!num) return "0"
    if (num >= 1e6) {
      return (num / 1e6).toFixed(1) + "M"
    } else if (num >= 1e3) {
      return (num / 1e3).toFixed(1) + "K"
    }
    return num.toString()
  }

  // Card for a single publisher-level platform (YouTube, X, etc.)
  const SocialPlatformCard = ({ platform, data, icon, color }) => {
    const hasFollowers = data && (data.followers || data.subscribers)
    const handle = data?.handle || null
    const followersCount = data?.followers || data?.subscribers || 0
    const followersDisplay = data?.followersDisplay || formatNumber(followersCount)
    const metricLabel = (data?.subscribers || platform === "YouTube") ? "Subscribers" : "Followers"
    const profileUrl = data?.url || null

    return (
      <Col md={6} lg={3} className="mb-3">
        <Card style={{
          height: "100%",
          background: "#21222D",
          borderRadius: "12px",
          border: "none"
        }}>
          <CardBody style={{
            height: "100%",
            display: "flex",
            flexDirection: "column",
            padding: "20px",
            justifyContent: "space-between"
          }}>
            <CardTitle className="h4 d-flex align-items-center gap-2 mb-3" style={{
              fontSize: "14px",
              fontWeight: 500,
              color: "rgba(255, 255, 255, 0.6)",
              margin: "0 0 16px"
            }}>
              <i className={icon} style={{ color: color, fontSize: "1.2rem" }}></i>
              {platform}
            </CardTitle>
            <div style={{ flex: 1 }}>
              {hasFollowers ? (
                <>
                  {handle && (
                    <p className="mb-2" style={{ color: "#fff", fontSize: "14px" }}>
                      <strong>Handle:</strong> {handle}
                    </p>
                  )}
                  <p className="mb-2" style={{ color: "#fff", fontSize: "14px" }}>
                    <strong>{metricLabel}:</strong> {followersDisplay}
                  </p>
                  {profileUrl && (
                    <a
                      href={profileUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary"
                      style={{ fontSize: "14px" }}
                    >
                      View Profile <i className="fas fa-external-link-alt" style={{ fontSize: "0.7rem" }}></i>
                    </a>
                  )}
                </>
              ) : (
                <p className="mb-0" style={{ color: "#fff", fontSize: "14px" }}>0</p>
              )}
            </div>
          </CardBody>
        </Card>
      </Col>
    )
  }

  // Table cell for socials of one platform (can show multiple handles)
  const SocialHandlesCell = ({ handles, platform }) => {
    if (!handles || !Array.isArray(handles) || handles.length === 0) {
      return <span style={{ color: "#b0b0b5" }}>-</span>
    }

    const platformMetadata = getPlatformMetadata(platform)

    return (
      <div style={{ display: "flex", flexDirection: "column", gap: "10px", padding: "4px 0" }}>
        {handles.map((handle, index) => (
          <div
            key={index}
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "3px",
              paddingBottom: index < handles.length - 1 ? "8px" : "0",
              borderBottom: index < handles.length - 1 ? "1px solid rgba(53, 55, 72, 0.5)" : "none"
            }}
          >
            <a
              href={handle.url}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                textDecoration: "none",
                display: "inline-flex",
                alignItems: "center",
                gap: "5px",
                fontSize: "0.9rem",
                fontWeight: 500,
                color: "#4A9EFF",
                transition: "opacity 0.2s"
              }}
              onMouseEnter={(e) => {
                e.target.style.opacity = "0.8"
                e.target.style.textDecoration = "underline"
              }}
              onMouseLeave={(e) => {
                e.target.style.opacity = "1"
                e.target.style.textDecoration = "none"
              }}
            >
              <i className={platformMetadata.icon} style={{ color: platformMetadata.color, fontSize: "0.85rem" }}></i>
              {handle.handle || "View Profile"}
              <i className="fas fa-external-link-alt" style={{ fontSize: "0.65rem", opacity: 0.6 }}></i>
            </a>
            {handle.followers && (
              <div style={{ fontSize: "0.8rem", marginLeft: "2px", lineHeight: "1.3", color: "#b0b0b5" }}>
                {formatNumber(handle.followers)} {platform === "youtube" ? "subscribers" : "followers"}
              </div>
            )}
          </div>
        ))}
      </div>
    )
  }

  // Table cell for website column (clickable link)
  const WebsiteCell = ({ website }) => {
    if (!website) {
      return <span style={{ color: "#b0b0b5" }}>-</span>
    }

    return (
      <a
        href={website}
        target="_blank"
        rel="noopener noreferrer"
        style={{
          textDecoration: "none",
          color: "#4A9EFF",
          fontSize: "0.95rem",
          fontWeight: 500,
          transition: "opacity 0.2s"
        }}
        onMouseEnter={(e) => {
          e.target.style.opacity = "0.8"
          e.target.style.textDecoration = "underline"
        }}
        onMouseLeave={(e) => {
          e.target.style.opacity = "1"
          e.target.style.textDecoration = "none"
        }}
      >
        {website}
        <i className="fas fa-external-link-alt" style={{ fontSize: "0.7rem", marginLeft: "6px", opacity: 0.7 }}></i>
      </a>
    )
  }


  // Build `MDBDataTable` config; platforms are fully dynamic
  const tableData = {
    columns: [
      {
        label: "Sno",
        field: "sno",
        sort: "asc",
        width: 50
      },
      {
        label: "Website",
        field: "website",
        sort: "asc",
      },
      // Dynamically generate columns for each available platform
      ...availablePlatforms.map(platformKey => {
        const metadata = getPlatformMetadata(platformKey)
        return {
          label: (
            <span style={{ display: "flex", alignItems: "center", gap: "6px" }}>
              <i className={metadata.icon} style={{ color: metadata.color, fontSize: "1.1rem" }}></i>
              {metadata.displayName}
            </span>
          ),
          field: platformKey,
          sort: "disabled",
        }
      })
    ],
    rows: appsSocialData.map((publisher, index) => {
      const row = {
        sno: index + 1,
        website: <WebsiteCell website={publisher.website} />,
      }

      // Dynamically add platform columns
      availablePlatforms.forEach(platformKey => {
        row[platformKey] = <SocialHandlesCell handles={publisher[platformKey]} platform={platformKey} />
      })

      return row
    }),
  }

  return (
    <React.Fragment>
      <SEO
        title="Publisher Socials"
        description="View social media profiles and studio links for publishers."
      />
      <div className="page-content m-3">
        {/* Publisher Header as Title */}
        <Row>
          <Col className="col-12">
            <div className="page-title-box">
              {/* Go Back button row (above logo + name, like /publisher-apps) */}
              <div style={{ marginBottom: "8px" }}>
                <button
                  type="button"
                  onClick={handleGoBack}
                  className="publisher-go-back-btn"
                  style={{
                    display: "inline-block",
                    background: "#2a2b37",
                    color: "#f43f5e",
                    padding: "5px 10px",
                    borderRadius: "6px",
                    fontWeight: 600,
                    textDecoration: "none",
                    transition: "0.2s",
                    whiteSpace: "nowrap",
                    border: "1px solid #3a3b47",
                    fontSize: "0.85rem",
                    cursor: "pointer",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = "#3a3b47"
                    e.currentTarget.style.color = "#fff"
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = "#2a2b37"
                    e.currentTarget.style.color = "#f43f5e"
                  }}
                >
                  &larr; Go Back
                </button>
              </div>

              {/* Publisher logo + name row */}
              {loader ? (
                <div className="publisher-header" style={{ marginBottom: 0 }}>
                  <div className="publisher-logo">
                    <div className="logo-circle">
                      <div className="logo-inner">
                        <div className="placeholder-glow">
                          <span className="placeholder col-6"></span>
                        </div>
                      </div>
                    </div>
                    <h1 className="publisher-name">
                      <div className="placeholder-glow">
                        <span className="placeholder col-6"></span>
                      </div>
                    </h1>
                  </div>
                </div>
              ) : (
                <div className="publisher-header" style={{ marginBottom: 0 }}>
                  <div className="publisher-logo">
                    <div className="logo-circle">
                      <div className="logo-inner">
                        {(() => {
                          const displayName = publisherName || "Unknown Publisher"
                          const words = displayName.split(' ')
                          const logoWords = words.slice(0, 2)
                          return (
                            <div className="logo-text">
                              {logoWords.map((word, index) => (
                                <div key={index} className="logo-word">
                                  {word.toUpperCase()}
                                </div>
                              ))}
                            </div>
                          )
                        })()}
                      </div>
                    </div>
                    <h1 className="publisher-name">
                      {publisherName || "Unknown Publisher"}
                    </h1>
                  </div>
                </div>
              )}
            </div>
          </Col>
        </Row>

        {/* Error Message */}
        {error && (
          <Row className="mt-3">
            <Col xs={12}>
              <div className="alert alert-danger" role="alert">
                {error}
              </div>
            </Col>
          </Row>
        )}

        {/* Publisher-Level Social Platforms */}
        {loader ? (
          <Row className="mt-2">
            <div className="d-flex justify-content-center">
              <Spinner size="lg" color="primary" />
            </div>
          </Row>
        ) : (
          <>
            <Row className="mt-2">
              {availablePlatforms.map((platformKey) => {
                const metadata = getPlatformMetadata(platformKey)
                const platformData = publisherSocials[platformKey]
                return (
                  <SocialPlatformCard
                    key={platformKey}
                    platform={metadata.displayName}
                    data={platformData}
                    icon={metadata.icon}
                    color={metadata.color}
                  />
                )
              })}
            </Row>

            {/* Individual Apps Social Information Table */}
            <Row className="mt-4">
              <Col xs={12}>
                <Card style={{ background: "transparent", border: "none" }}>
                  <CardBody style={{ padding: 0 }}>
                    {appsSocialData.length > 0 ? (
                      <div className="mt-3 publisher-socials-table-wrapper">
                        <MDBDataTable
                          responsive
                          bordered
                          data={tableData}
                          noBottomColumns
                          paging={false}
                          searching={false}
                          displayEntries={false}
                          info={false}
                        />
                        <style>{`
                          .publisher-socials-table-wrapper {
                            overflow-x: hidden;
                          }
                          
                          .publisher-socials-table-wrapper .dataTables_wrapper {
                            background: transparent;
                            overflow-x: hidden;
                          }
                          
                          .publisher-socials-table-wrapper table.dataTable {
                            width: 100%;
                            border-collapse: collapse;
                            border: 1px solid #353748;
                            border-radius: 8px;
                            overflow: hidden;
                            background: transparent;
                            table-layout: auto;
                          }
                          
                          .publisher-socials-table-wrapper table.dataTable thead {
                            background: #2a2b37;
                          }
                          
                          .publisher-socials-table-wrapper table.dataTable thead th {
                            background: #2a2b37;
                            color: #b0b0b5;
                            font-weight: 500;
                            font-size: 14px;
                            padding: 16px 12px;
                            text-align: left;
                            border-bottom: 1px solid #353748;
                            white-space: normal;
                            position: relative;
                            word-wrap: break-word;
                          }
                          
                          .publisher-socials-table-wrapper table.dataTable thead th.sorting,
                          .publisher-socials-table-wrapper table.dataTable thead th.sorting_asc,
                          .publisher-socials-table-wrapper table.dataTable thead th.sorting_desc {
                            cursor: pointer;
                            user-select: none;
                            transition: background-color 0.2s;
                          }
                          
                          .publisher-socials-table-wrapper table.dataTable thead th.sorting:hover,
                          .publisher-socials-table-wrapper table.dataTable thead th.sorting_asc:hover,
                          .publisher-socials-table-wrapper table.dataTable thead th.sorting_desc:hover {
                            background: #3a3b47;
                          }
                          
                          .publisher-socials-table-wrapper table.dataTable tbody td {
                            background: transparent;
                            color: #fff;
                            font-size: 14px;
                            padding: 12px;
                            border-bottom: 1px solid #353748;
                            vertical-align: top;
                          }
                          
                          .publisher-socials-table-wrapper table.dataTable tbody tr {
                            background: transparent;
                            transition: background-color 0.2s;
                          }
                          
                          .publisher-socials-table-wrapper table.dataTable tbody tr:hover {
                            background: #2a2b37;
                          }
                          
                          .publisher-socials-table-wrapper table.dataTable tbody tr:last-child td {
                            border-bottom: none;
                          }
                          
                          /* Social media icons in headers */
                          .publisher-socials-table-wrapper table.dataTable thead th i.fab {
                            margin-right: 6px;
                            font-size: 1.1rem;
                          }
                          
                          /* Sort arrows styling */
                          .publisher-socials-table-wrapper table.dataTable thead th.sorting:before,
                          .publisher-socials-table-wrapper table.dataTable thead th.sorting:after {
                            opacity: 0.3;
                            color: #b0b0b5;
                          }
                          
                          .publisher-socials-table-wrapper table.dataTable thead th.sorting_asc:before,
                          .publisher-socials-table-wrapper table.dataTable thead th.sorting_desc:after {
                            opacity: 1;
                            color: #F43F5E;
                          }
                        `}</style>
                      </div>
                    ) : (
                      <div className="text-center py-4">
                        <p className="text-muted mb-0">No social accounts found for individual apps.</p>
                      </div>
                    )}
                  </CardBody>
                </Card>
              </Col>
            </Row>
          </>
        )}
      </div>
    </React.Fragment>
  )
}
